/**
 * Navigation Service - Abstract layer for routing
 *
 * This service provides a unified interface for navigation operations,
 * making it easy to switch between different routing solutions (UmiJS -> Next.js, etc.)
 */

import { history } from 'umi';

export interface NavigationLocation {
  pathname: string;
  search?: string;
  hash?: string;
  state?: any;
}

export interface NavigationService {
  push: (path: string | NavigationLocation) => void;
  replace: (path: string | NavigationLocation) => void;
  go: (delta: number) => void;
  back: () => void;
  forward: () => void;
  getCurrentLocation: () => NavigationLocation;
  getQuery: () => Record<string, any>;
}

/**
 * UmiJS implementation of navigation service
 */
class UmiNavigationService implements NavigationService {
  push(path: string | NavigationLocation): void {
    if (!history) {
      console.warn('Navigation service: history not available');
      return;
    }
    if (typeof path === 'string') {
      history.push(path);
    } else {
      history.push(path);
    }
  }

  replace(path: string | NavigationLocation): void {
    if (!history) {
      console.warn('Navigation service: history not available');
      return;
    }
    if (typeof path === 'string') {
      history.replace(path);
    } else {
      history.replace(path);
    }
  }

  go(delta: number): void {
    if (!history) {
      console.warn('Navigation service: history not available');
      return;
    }
    history.go(delta);
  }

  back(): void {
    if (!history) {
      console.warn('Navigation service: history not available');
      return;
    }
    history.goBack();
  }

  forward(): void {
    if (!history) {
      console.warn('Navigation service: history not available');
      return;
    }
    history.goForward();
  }

  getCurrentLocation(): NavigationLocation {
    const location = history?.location;
    if (!location) {
      return {
        pathname: '/',
        search: '',
        hash: '',
        state: null,
      };
    }
    return {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
      state: location.state,
    };
  }

  getQuery(): Record<string, any> {
    const location = history?.location;
    if (!location || !location.search) {
      return {};
    }
    const searchParams = new URLSearchParams(location.search);
    const query: Record<string, any> = {};

    for (const [key, value] of searchParams.entries()) {
      query[key] = value;
    }

    return query;
  }
}

/**
 * Future Next.js implementation placeholder
 * This would be implemented when migrating to Next.js
 */
// class NextNavigationService implements NavigationService {
//   push(path: string | NavigationLocation): void {
//     // import { useRouter } from 'next/router';
//     // const router = useRouter();
//     // router.push(path);
//   }
//
//   replace(path: string | NavigationLocation): void {
//     // router.replace(path);
//   }
//
//   // ... other methods
// }

// Export singleton instance
export const navigationService: NavigationService = new UmiNavigationService();

// Convenience exports for common operations
export const navigateTo = (path: string | NavigationLocation) => navigationService.push(path);
export const redirectTo = (path: string | NavigationLocation) => navigationService.replace(path);
export const goBack = () => navigationService.back();
export const goForward = () => navigationService.forward();
export const getCurrentPath = () => navigationService.getCurrentLocation().pathname;
export const getSearchParams = () => navigationService.getQuery();

// Legacy compatibility - these can be used as drop-in replacements
export const legacyHistory = {
  push: navigationService.push,
  replace: navigationService.replace,
  go: navigationService.go,
  goBack: navigationService.back,
  goForward: navigationService.forward,
  get location() {
    return navigationService.getCurrentLocation();
  },
};

export default navigationService;